import type React from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Upload, CheckCircle, CloudCog, File } from "lucide-react"
import { Job } from "../../../src/swagger"
import { candidateApi } from "../../../src/utils/configuration"
import setValue from "react-hook-form" // at your imports (if not already)

interface JobApplicationFormProps {
  job: Partial<Pick<Job, "id" | "title" | "companyName">>
  onClose: () => void
}

interface FormValues {
  name: string
  email: string
  phone: string
  coverLetter: string
  resume: FileList
}

export function JobApplicationForm({ job, onClose }: JobApplicationFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<FormValues>({
    mode : 'onChange'
  })

  const [isSubmitted, setIsSubmitted] = useState(false)

  const onSubmit = async (data: FormValues) => {
    const file = data.resume?.[0]
    if (!file) {
      setError("resume", { message: "Resume is required" })
      return
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]
    const maxSize = 5 * 1024 * 1024

    if (!allowedTypes.includes(file.type)) {
      setError("resume", { message: "Please upload a PDF or Word document" })
      return
    }

    if (file.size > maxSize) {
      setError("resume", { message: "File size must be less than 5MB" })
      return
    }

    await candidateApi.candidateApplyJob({
      resume: file,
      name: data.name,
      email: data.email,
      phone: data.phone,
      coverLetter: data.coverLetter ?? '',
      jobId: job.id
    })

    setIsSubmitted(true)
  }

  const selectedResume = watch("resume")?.[0]

  if (isSubmitSuccessful || isSubmitted) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Submitted Successfully!</h3>
            <p className="text-gray-600 mb-6">
              Thank you for applying to {job.title} at {job.companyName}. We'll review your application and get back to you
              soon.
            </p>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Apply for {job.title}</DialogTitle>
          <p className="text-sm text-gray-600">{job.companyName}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name <span className="text-red-600">*</span></Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              className={errors.name ? "border-red-500" : ""}
              {...register("name", { required: "Name is required" })}
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address <span className="text-red-600">*</span></Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              className={errors.email ? "border-red-500" : ""}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              })}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number <span className="text-red-600">*</span></Label>
            <Input
              id="phone"
              type="text"
              placeholder="Enter your phone number"
              className={errors.phone ? "border-red-500" : ""}
              {...register("phone", {
                required: "Phone number is required",
                pattern: {
                  value: /^\d{10}$/,
                  message: "Please enter a valid 10-digit phone number (numbers only)",
                },
              })}
            />
            {errors.phone && <p className="text-sm text-red-500">{errors.phone.message}</p>}
          </div>

          {/* Resume Upload */}
          <div className="space-y-2">
            <Label htmlFor="resume">Resume <span className="text-red-600">*</span></Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                id="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                {...register("resume")}
              />
              <label htmlFor="resume" className="cursor-pointer">
                {
                  selectedResume ? <File className="h-8 w-8 text-gray-400 mx-auto mb-2" /> :
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                }
                <p className="text-sm text-gray-600 mb-1">
                  {selectedResume ? selectedResume.name : "Click to upload your resume"}
                </p>
                {
                  selectedResume ?
                    <p className="text-xs text-gray-500">Document selected successfully</p>

                    :
                    <p className="text-xs text-gray-500">PDF, DOC, or DOCX (max 5MB)</p>
                }
              </label>
            </div>
            {errors.resume && <p className="text-sm text-red-500">{errors.resume.message as string}</p>}
          </div>

          {/* Cover Letter */}
          <div className="space-y-2">
            <Label htmlFor="coverLetter">Cover Letter</Label>
            <Textarea
              id="coverLetter"
              placeholder="Tell us why you're interested in this position and what makes you a great fit..."
              rows={6}
              className={errors.coverLetter ? "border-red-500" : ""}
              {...register("coverLetter",
                {
                  minLength: { value: 10, message: "Minimum 10 characters" },
                })}
            />
            {errors.coverLetter && <p className="text-sm text-red-500">{errors.coverLetter.message}</p>}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 bg-transparent" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className={`flex-1 text-white
              ${isSubmitting || !isValid || !selectedResume
                            ? "bg-gray-400 cursor-not-allowed text-gray-200 hover:bg-gray-400"
                            : "bg-indigo-600 hover:bg-indigo-700"}
            `}
              disabled={isSubmitting || !isValid || !selectedResume}
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
